import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Footer from './Footer';
import CookieConsent from './CookieConsent';
import { handleError } from '../utils/logger';
import './Auth.css';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const { signIn, signUp } = useAuth();

  useEffect(() => {
    // Check if cookies were previously accepted
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent === 'accepted') {
      setCookiesAccepted(true);
    }
  }, []);
  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleForgotPassword = async () => {
    setError('');
    setMessage('');

    if (!email) {
      setError('Enter your email above before requesting a reset link.');
      return;
    }

    try {
      setForgotLoading(true);
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setMessage('Check your email for a password reset link.');
    } catch (err) {
      setError(handleError(err, 'Failed to send reset email. Please try again.'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleCookieAccept = () => {
    setCookiesAccepted(true);
  };

  const handleCookieDecline = () => {
    setError('You must accept cookies to use this application. Cookies are required for authentication and tracking usage.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cookiesAccepted) {
      setError('You must accept cookies to continue. Please accept the cookie consent to login.');
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (isLogin) {
        await signIn(email, password);
      } else {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        const passwordError = validatePassword(password);
        if (passwordError) {
          setError(passwordError);
          setLoading(false);
          return;
        }
        await signUp(email, password);
        setMessage('Check your email to confirm your account!');
      }
    } catch (err) {
      // Handle authentication errors gracefully - don't log expected user errors
      const errorMessage = err?.message || '';
      const isAuthError = 
        errorMessage.includes('Invalid login credentials') || 
        errorMessage.includes('Email not confirmed') ||
        errorMessage.includes('Invalid credentials') ||
        errorMessage.includes('Invalid login') ||
        err?.status === 400; // Supabase returns 400 for invalid credentials
      
      if (isAuthError) {
        // These are expected user errors, just show user-friendly message
        // Don't log to console - these are normal user input errors
        setError('Invalid email or password. Please check your credentials and try again.');
      } else {
        // For unexpected errors, use handleError which logs them
        setError(handleError(err, 'Authentication failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <CookieConsent 
        onAccept={handleCookieAccept}
        onDecline={handleCookieDecline}
      />
      <div className="auth-wrapper">
        <div className="auth-card">
          <div className="auth-card-header">
            <h2 className="auth-title">
              <img 
                src="https://vmfkqkzaqslniowpftts.supabase.co/storage/v1/object/public/img/featuremap_logo_white.png" 
                alt="FeatureMap Logo" 
                className="auth-logo"
              />
              FeatureMap
            </h2>
            <p>Sign In or Sign Up to start organizing your product features!</p>
          </div>

          {error && <div className="auth-error">{error}</div>}
          {message && <div className="auth-message">{message}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                minLength={6}
              />
            </div>

            {!isLogin && (
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                  minLength={6}
                />
              </div>
            )}

            <button type="submit" className="auth-button" disabled={loading || !cookiesAccepted}>
              {loading ? 'Loading...' : isLogin ? 'Sign In' : 'Sign Up'}
            </button>
            {!cookiesAccepted && (
              <p className="cookie-required-message">
                You must accept cookies to continue.
              </p>
            )}

            {isLogin && (
              <button
                type="button"
                className="link-button forgot-password-link"
                onClick={handleForgotPassword}
                disabled={forgotLoading}
              >
                {forgotLoading ? 'Sending reset link...' : 'Forgot your password?'}
              </button>
            )}
          </form>

          {!isLogin && (
            <div className="auth-switch">
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setIsLogin(true);
                  setError('');
                  setMessage('');
                }}
              >
                Already have an account? Sign in
              </button>
            </div>
          )}
        </div>

        <div className="auth-info-panel">
          <h3>Why FeatureMap?</h3>
          <div className="auth-info-item">
            <div className="auth-info-icon">
              <i className="fas fa-sitemap"></i>
            </div>
            <div>
              <h4>Track your feature hierarchy</h4>
              <p>Capture every product idea in a structured tree so nothing gets lost.</p>
            </div>
          </div>
          <div className="auth-info-item">
            <div className="auth-info-icon">
              <i className="fas fa-link"></i>
            </div>
            <div>
              <h4>Connect documentation</h4>
              <p>Easily link existing docs so your internal teams always have the right context.</p>
            </div>
          </div>
          <div className="auth-info-item">
            <div className="auth-info-icon">
              <i className="fas fa-users"></i>
            </div>
            <div>
              <h4>Engage customers</h4>
              <p>Share an external view so customers can vote or rate the features that matter most.</p>
            </div>
          </div>
          <div className="auth-info-cta">
            <p>Ready to get started?</p>
            <button
              type="button"
              className="auth-info-button"
              onClick={() => {
                if (isLogin) {
                  setIsLogin(false);
                  setError('');
                  setMessage('');
                }
              }}
            >
              Signup to be part of the Preview
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Auth;
